import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button, Table as BTable, Spinner } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import {
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

// Componente de tabela para cada grupo
function GroupTable({ data, columns, sorting, setSorting }) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
    onSortingChange: setSorting,
    defaultColumn: {
      sortDescFirst: false,
    },
  });

  return (
    <BTable hover responsive size="md" className="tabelas">
      <thead className="tabel-cabecalho">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <th
                onClick={header.column.getToggleSortingHandler()}
                key={header.id}
                colSpan={header.colSpan}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                {index !== headerGroup.headers.length - 1
                  ? ({
                      asc: <i className="icn-header bi bi-arrow-up" />,
                      desc: <i className="icn-header bi bi-arrow-down" />,
                    }[header.column.getIsSorted()] ?? (
                      <i className="icn-header bi bi-arrow-down-up" />
                    ))
                  : ""}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </BTable>
  );
}
import ErrorModal from "../../../components/modalError";
import DeleteConfirmationModal from "../../../components/modalDelete";
import PropTypes from "prop-types";
import CSVButton from "../../../components/CSVButton";
import { useTranslation } from "react-i18next";
import useFormValidation from "../../../hooks/useFormValidation";
import EditCodecModal from "./modals/EditGroupCodec";
import EditCodecInputModal from "./modals/EditCodecModal";
import NoDataTables from "../../../components/noDataTables";

function CodecTabela({
  onDataDeleted,
  onDataEdited,
  codecs,
  strategy,
  input,
  onDataAdded,
  tableUpdated,
  setTableUpdated,
  deleteData,
  updateData,
  fetchData,
  postData,
  setShowModal,
}) {
  const { t } = useTranslation();

  // Estados principais
  const [codecsByGroup, setCodecsByGroup] = useState({});
  const [loadingGroups, setLoadingGroups] = useState({});
  const [dataCodecGroup, setDataCodecGroup] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [showModalDelete, setShowModalDelete] = useState(false);
  // Separar os modais:
  const [showEditCodecModal, setShowEditCodecModal] = useState(false);
  const [showEditCodecInputModal, setShowEditCodecInputModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [originalEditItem, setOriginalEditItem] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [selectedCodecId, setSelectedCodecId] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newStrategy, setNewStrategy] = useState("");
  const [error, setError] = useState("");
  const [sorting, setSorting] = useState([]);

  const isButtonDisabled = useFormValidation();

  // Efeitos para atualizar dados
  useEffect(() => setDataCodecGroup(codecs), [codecs]);

  const loadMappingsForGroup = useCallback(
    async (groupId) => {
      if (codecsByGroup[groupId] || loadingGroups[groupId]) return;

      setLoadingGroups((prev) => ({ ...prev, [groupId]: true }));
      try {
        const response = await fetchData(`/codec-inputs/codecs/${groupId}`);
        if (response && response.data) {
          setCodecsByGroup((prev) => ({ ...prev, [groupId]: response.data }));
        }
      } catch (error) {
        console.error("Erro ao buscar inputs do codec:", error);
      } finally {
        setLoadingGroups((prev) => ({ ...prev, [groupId]: false }));
      }
    },
    [fetchData, codecsByGroup, loadingGroups],
  );

  const handleAccordionSelect = (eventKey) => {
    setActiveKey(eventKey);
    if (eventKey) {
      loadMappingsForGroup(eventKey);
    }
  };

  useEffect(() => {
    if (!activeKey) return;
    if (!tableUpdated) return;

    loadMappingsForGroup(activeKey);
  }, [tableUpdated]);

  // Modal de edição de grupo
  const openEditCodecModal = (group) => {
    setEditGroupId(group.id);
    setNewName(group.name);
    setNewDescription(group.description || "");
    setNewStrategy(group.decoding_strategy.id || "");
    setShowEditCodecModal(true);
    setError("");
  };

  const closeEditCodecModal = () => {
    setShowEditCodecModal(false);
    setError("");
  };

  // Modal de confirmação de cópia
  const openCopyConfirmModal = (groupId) => {
    setSelectedCodecId(groupId);
    setShowConfirmModal(true);
  };

  // Modal de confirmação de exclusão de grupo
  const openDeleteGroupModal = (groupId) => {
    setDeleteGroupId(groupId);
    setShowConfirmDeleteModal(true);
  };

  const closeDeleteGroupModal = () => {
    setDeleteGroupId(null);
    setShowConfirmDeleteModal(false);
  };

  // Modal de erro
  const closeErrorModal = () => {
    setShowErrorModal(false);
    setError("");
  };

  // Modal de edição/adicionar codec
  const openEditCodecInputModal = useCallback(
    (isEdit = false, item = null, groupId = null) => {
      setIsEditMode(isEdit);
      if (isEdit && item) {
        const formatted = {
          input_id: item.input.id,
          data_type: item.data_type,
          input_code: item.input_code,
          byte_length: item.byte_length,
          codec_id: groupId,
        };

        setEditItem({ ...formatted });
        setOriginalEditItem({ ...formatted });
        setEditId(item.id);
      } else {
        setEditItem({
          input_id: null,
          data_type: null,
          input_code: null,
          byte_length: null,
          codec_id: groupId,
        });
        setEditId(null);
        setOriginalEditItem(null);
      }
      setShowEditCodecInputModal(true);
      setError("");
    },
    [],
  );

  const closeEditCodecInputModal = () => {
    setShowEditCodecInputModal(false);
    setEditItem(null);
    setEditId(null);
    setIsEditMode(false);
    setError("");
  };

  // Handlers principais
  const handleEditGroup = async (e) => {
    e.preventDefault();

    if (newName.length > 40)
      return setError(t("O nome deve ter no máximo 40 caracteres."));
    if (!newName) return setError(t("O nome é obrigatório."));

    try {
      const response = await updateData(`/codecs/${editGroupId}`, {
        name: newName,
        description: newDescription,
        decoding_strategy_id: newStrategy,
      });

      setDataCodecGroup((groups) =>
        groups.map((g) =>
          g.id === editGroupId
            ? {
                ...g,
                name: newName,
                description: newDescription,
                decoding_strategy: response.data.decoding_strategy,
              }
            : g,
        ),
      );
      closeEditCodecModal();
    } catch (err) {
      if (err.response?.status === 409) setError(t("Esse nome já está em uso"));
      else console.error("Erro ao editar codec:", err);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await deleteData(`/codecs/${id}`);
      setDataCodecGroup((groups) => groups.filter((g) => g.id !== id));
      closeDeleteGroupModal();
    } catch (err) {
      if (err.status === 409) {
        setError(
          t(
            "Este codec não pode ser excluído, pois está sendo usado por algum ativo",
          ),
        );
        setShowErrorModal(true);
        closeDeleteGroupModal();
      } else {
        setError(t("Erro ao deletar codec."));
        setShowErrorModal(true);
      }
    }
  };

  const handleEditMapping = useCallback(
    (item) => {
      setError("");
      openEditCodecInputModal(true, item);
    },
    [openEditCodecInputModal],
  );

  const handleDeleteMapping = useCallback((id) => {
    setDeleteId(id);
    setShowModalDelete(true);
  }, []);

  const confirmDeleteMapping = async () => {
    try {
      await deleteData(`/codec-inputs/${deleteId}`);
      // Atualizar o estado local removendo o codec deletado
      setCodecsByGroup((prev) => {
        const updated = { ...prev };
        for (const groupId in updated) {
          if (updated[groupId]) {
            updated[groupId] = updated[groupId].filter(
              (item) => item.id !== deleteId,
            );
          }
        }
        return updated;
      });
      onDataDeleted(deleteId);
    } catch {
      setError("Erro ao deletar item");
      setShowErrorModal(true);
    } finally {
      setShowModalDelete(false);
    }
  };
  const handleEditMappingSubmit = async (formData) => {
    await handleEditMappingSubmitConfirmed(formData);
  };

  const handleEditMappingSubmitConfirmed = async (item) => {
    if (isEditMode) {
      try {
        const requestBody = {
          input_id: item.input_id,
          data_type: item.data_type,
          input_code: item.input_code,
          byte_length: item.byte_length,
        };

        const response = await updateData(
          `/codec-inputs/${editId}`,
          requestBody,
        );

        const groupId = response.data.codec?.id || item.codec_id;

        // Atualizar sem triggar tableUpdated (preserva activeKey)
        setCodecsByGroup((prev) => ({
          ...prev,
          [groupId]: (prev[groupId] || []).map((m) =>
            m.id === editId ? response.data : m,
          ),
        }));

        onDataEdited(response.data);
        closeEditCodecInputModal();
      } catch (err) {
        if (err.status === 400) setError(t("Input já cadastrado"));
        console.error("Erro ao editar item:", err);
      }
    } else {
      const codecsIdToUse = item.codec_id || selectedCodecId;
      const requestObject = {
        codec_id: codecsIdToUse,
        input_id: item.input_id,
        data_type: item.data_type,
        input_code: item.input_code,
        byte_length: item.byte_length,
      };
      if (!requestObject.codec_id) return setError(t("Selecione um codec."));
      try {
        const response = await postData("/codec-inputs", [requestObject]);
        const newMappings = Array.isArray(response.data)
          ? response.data
          : [response.data];

        const enrichedMappings = newMappings.map((mapping) => ({
          ...mapping,
          input: input.find((i) => i.id === mapping.input_id) || mapping.input,
        }));

        // Só adiciona ao estado se o grupo já foi carregado (expandido)
        setCodecsByGroup((prev) => {
          if (!prev[codecsIdToUse]) {
            return prev;
          }
          return {
            ...prev,
            [codecsIdToUse]: [...prev[codecsIdToUse], ...enrichedMappings],
          };
        });
        onDataAdded(enrichedMappings);
        if (
          response.data?.codec &&
          !dataCodecGroup.some((g) => g.id === response.data.codec.id)
        ) {
          setDataCodecGroup((groups) => [...groups, response.data.codec]);
        }
        closeEditCodecInputModal();
      } catch (err) {
        if (err.response?.status === 400) {
          setError(t("Input já está sendo utilizada nesse codec."));
        } else {
          setError(t("Ocorreu um erro ao criar o codec."));
        }
      }
    }
  };

  const filteredOptions = useMemo(() => {
    if (!editItem) {
      return { filteredInputs: [] };
    }

    const groupId = editItem.codec_id;

    const usedMappings = codecsByGroup[groupId] || [];

    const usedInputIds = usedMappings
      .filter((m) => m.input)
      .map((m) => m.input.id);

    const filteredInputs = input.filter((i) => {
      if (isEditMode && i.id === originalEditItem?.input_id) return true;
      return !usedInputIds.includes(i.id);
    });
    const inputOptions = filteredInputs.map((i) => ({
      label: i.name,
      value: i.id,
    }));

    return { inputOptions };
  }, [editItem, codecsByGroup, input, isEditMode, originalEditItem]);

  // Cópia de codec
  const copyMapping = async () => {
    setIsCopying(true);
    try {
      const mappingsToCopy = await fetchMappings();
      if (mappingsToCopy.length === 0) {
        setError(t("Para copiar o codecs ele precisa ter ao menos um codec"));
        setShowErrorModal(true);
        setShowConfirmModal(false);
        return;
      }
      const newGroupId = await createNewMappingGroup(
        mappingsToCopy[0].codec.name,
        mappingsToCopy[0].codec.description,
        mappingsToCopy[0].codec.decoding_strategy_id,
      );
      await copyMappings(mappingsToCopy, newGroupId);
      setDataCodecGroup((groups) => [
        ...groups,
        {
          id: newGroupId,
          name: `Cópia do ${mappingsToCopy[0].codec.name}`,
          description: `${mappingsToCopy[0].codec.description}`,
          decoding_strategy_id: `${mappingsToCopy[0].codec.decoding_strategy_id}`,
        },
      ]);
      setTableUpdated((prev) => !prev);
    } catch (err) {
      setError(t(err));
      setShowErrorModal(true);
      console.error("Erro ao copiar codec:", err);
    } finally {
      setIsCopying(false);
      setShowConfirmModal(false);
    }
  };

  const fetchMappings = async (id = selectedCodecId) => {
    try {
      const response = await fetchData(`/codec-inputs/codecs/${id}`);
      return response.data;
    } catch (err) {
      console.error("Erro ao enviar comando:", err);
      throw new Error(t("Erro ao buscar codecs"));
    }
  };

  const createNewMappingGroup = async (
    groupName,
    groupDescription,
    decoding_strategy_id,
  ) => {
    try {
      const response = await postData("/codecs", {
        name: `Cópia do ${groupName}`,
        description: `${groupDescription}`,
        decoding_strategy_id: decoding_strategy_id,
      });
      return response.data.id;
    } catch (err) {
      if (err.response?.status === 409)
        throw new Error(t("Esse codec já foi copiado anteriormente"));
      else throw new Error(t("Erro ao criar novo codec"));
    }
  };

  const copyMappings = async (mappingsToCopy, newGroupId) => {
    try {
      const mappingsWithGroupId = mappingsToCopy.map((codecInput) => ({
        codec_id: newGroupId,
        input_id: codecInput.input ? codecInput.input.id : null,
        data_type: codecInput.data_type,
        input_code: codecInput.input_code,
        byte_length: codecInput.byte_length,
      }));
      const response = await postData("/codec-inputs", mappingsWithGroupId);
      return response.data;
    } catch (err) {
      setError(t("Erro ao copiar codec"));
      setShowErrorModal(true);
      console.error("Erro ao copiar codec:", err);
    }
  };

  // Exportação CSV
  const exportToCSV = useCallback(
    (groupId) => {
      const groupMappings = codecsByGroup[groupId] || [];
      return groupMappings.map((codecInput) => ({
        Nome: codecInput.codec?.name,
        input_id: codecInput.input ? codecInput.input.id : null,
        data_type: codecInput.data_type,
        input_code: codecInput.input_code,
        byte_length: codecInput.byte_length,
      }));
    },
    [codecsByGroup],
  );

  // Definição das colunas da tabela por grupo (memoizado)
  const columnsPerGroup = useMemo(
    () =>
      dataCodecGroup.reduce((acc, group) => {
        acc[group.id] = [
          {
            header: t("Input"),
            accessorFn: (row) => row.input?.name ?? "-",
            id: "input_name",
          },
          {
            header: t("Tipo de dado"),
            accessorKey: "data_type",
            accessorFn: (row) => row.data_type ?? "-",
          },
          {
            header: t("Código de input"),
            accessorKey: "input_code",
            accessorFn: (row) => row.input_code ?? "-",
          },
          {
            header: t("Tamanho do byte"),
            accessorKey: "byte_length",
            accessorFn: (row) => row.byte_length ?? "-",
          },
          {
            id: "acoes",
            header: (
              <div className="header-action">
                <CSVButton
                  data={exportToCSV(group.id)}
                  filename={`Codec_${group.name}.csv`}
                />
              </div>
            ),
            cell: (info) => (
              <div
                className="d-flex justify-content-end align-items-center"
                style={{ textWrapMode: " nowrap" }}
              >
                <div>
                  <button
                    className="btn-table btn-editar bi bi-pencil-square"
                    onClick={() => handleEditMapping(info.row.original)}
                  ></button>
                  <button
                    className="btn-table btn-delete bi bi-trash3"
                    onClick={() => handleDeleteMapping(info.row.original.id)}
                  ></button>
                </div>
              </div>
            ),
          },
        ];
        return acc;
      }, {}),
    [dataCodecGroup, t, handleEditMapping, handleDeleteMapping, exportToCSV],
  );

  // Renderização
  return (
    <div className="p-2 div-table">
      {/* Modal editar grupo */}
      {showEditCodecModal && (
        <EditCodecModal
          show={showEditCodecModal}
          onHide={closeEditCodecModal}
          onSubmit={handleEditGroup}
          newName={newName}
          setNewName={setNewName}
          newDescription={newDescription}
          setNewDescription={setNewDescription}
          newStrategy={newStrategy}
          setNewStrategy={setNewStrategy}
          error={error}
          t={t}
          strategyOptions={strategy.map((s) => ({
            label: s.name,
            value: s.id,
          }))}
        />
      )}

      {/* Modal editar/adicionar codec */}
      {showEditCodecInputModal && (
        <EditCodecInputModal
          show={showEditCodecInputModal}
          onHide={closeEditCodecInputModal}
          onSubmit={handleEditMappingSubmit}
          isEditMode={isEditMode}
          t={t}
          input={filteredOptions.inputOptions}
          initialItem={editItem}
          error={error}
          isButtonDisabled={isButtonDisabled}
        />
      )}
      {/* Modal erro */}
      {showErrorModal && (
        <ErrorModal
          showErrorModal={showErrorModal}
          handleCloseErrorModal={closeErrorModal}
          errorMessage={error}
        />
      )}

      {/* Modal confirmação exclusão grupo */}
      {showConfirmDeleteModal && (
        <DeleteConfirmationModal
          show={showConfirmDeleteModal}
          onClose={closeDeleteGroupModal}
          onConfirm={() => handleDeleteGroup(deleteGroupId)}
          pagina={t("codec")}
        />
      )}

      {/* Modal confirmação exclusão codec */}
      {showModalDelete && (
        <DeleteConfirmationModal
          show={showModalDelete}
          onClose={() => setShowModalDelete(false)}
          onConfirm={confirmDeleteMapping}
          pagina={t("vínculo de input ao codec")}
        />
      )}

      {/* Header ações */}
      <div className="header-action">
        <button
          onClick={() => setShowModal(true)}
          className="btn-table bi bi-plus-circle"
        ></button>
      </div>

      {/* Accordion de grupos */}
      <Accordion
        className="mt-3"
        activeKey={activeKey}
        onSelect={handleAccordionSelect}
      >
        {dataCodecGroup.map((map) => (
          <Accordion.Item key={map.id} eventKey={map.id}>
            <div className="accordion-cabecalho">
              <Accordion.Header>{map.name}</Accordion.Header>
              <div className="accordion-itens">
                <button
                  className="btn-table accordion-edit btn-editar bi bi-plus-circle"
                  onClick={() => openEditCodecInputModal(false, null, map.id)}
                ></button>
                <button
                  className="btn-table accordion-edit btn-editar bi bi-pencil-square"
                  onClick={() => openEditCodecModal(map)}
                ></button>
                <button
                  onClick={() => openCopyConfirmModal(map.id)}
                  className="btn-table bi bi-copy"
                ></button>
                <button
                  className="btn-table accordion-edit btn-delete bi bi-trash3"
                  onClick={() => openDeleteGroupModal(map.id)}
                ></button>
              </div>
            </div>
            <Accordion.Body className="accordion-content">
              {" "}
              <div>
                {map?.decoding_strategy?.id && (
                  <>Decodificação: {map.decoding_strategy.name}</>
                )}
              </div>
              {map?.description && (
                <>
                  <span className="text-body-secondary text-break">
                    Descrição: {map.description}
                  </span>
                </>
              )}
              <Card.Body>
                {loadingGroups[map.id] ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : codecsByGroup[map.id]?.length > 0 ? (
                  <GroupTable
                    data={codecsByGroup[map.id] || []}
                    columns={columnsPerGroup[map.id] || []}
                    sorting={sorting}
                    setSorting={setSorting}
                  />
                ) : (
                  <NoDataTables message="Sem input cadastrado para este codec" />
                )}
              </Card.Body>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {/* Modal confirmação cópia */}
      {showConfirmModal && (
        <Modal
          backdrop="static"
          centered
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
        >
          <Modal.Header closeVariant="white" closeButton>
            <Modal.Title>{t("confirm_copy")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t("Deseja realmente copiar esse codec?")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={copyMapping}
              disabled={isCopying}
            >
              {isCopying ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("Copiar")
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

CodecTabela.propTypes = {
  variable: PropTypes.array,
  input: PropTypes.array,
  codecs: PropTypes.array,
  onDataDeleted: PropTypes.func,
  onDataEdited: PropTypes.func,
  onDataAdded: PropTypes.func,
  onShowModal: PropTypes.func,
  tableUpdated: PropTypes.bool,
  setTableUpdated: PropTypes.func,
  deleteData: PropTypes.func,
  updateData: PropTypes.func,
  postData: PropTypes.func,
  fetchData: PropTypes.func,
  setShowModal: PropTypes.func,
};

export default CodecTabela;
